import * as React from "react";
import { observer } from "mobx-react-lite";
import { mk8sDryRun } from "../../../../../mobxStores/dryRun/mk8s";
import { Mk8sDraftMobx } from "../../../../../mst/stores/mk8s.draft";
import { homeLink, request } from "../../../../../services/cpln";
import { DryRunAlert } from "../../../dryRunAlert";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { NGInput } from "../../../../../newcomponents/input/input";
import { NGSelect } from "../../../../../newcomponents/select/ngselect";
import { NGButton } from "../../../../../newcomponents/button/Button";
import { NGFormElement } from "../../../../../newcomponents/ngformelement/ngformelement";
import { useNGFormContext } from "../../../../../reactContexts/ngFormContext";
import { NGFormLabel } from "../../../../../newcomponents/text/formLabel";
import { NGInputListMst } from "../../../../../newcomponents/inputList/inputListMst";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { getDryRunErrorText } from "../../../getDryRunErrorText";
import { MoreHorizontal } from "react-feather";
import { NGSwitch } from "../../../../../newcomponents/switch";
import { NGRadioGroup } from "../../../../../newcomponents/radioGroup";
import { NGLabelText } from "../../../../../newcomponents/text/labelText";

type RouteParams = "index";

interface Props {
  mk8sDraft: Mk8sDraftMobx;
}
const Mk8sCreateProviderGCPNodePoolRaw: React.FC<Props> = ({ mk8sDraft }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [browserKey, setBrowserKey] = React.useState("");

  const formData = useNGFormContext();

  const { index: indexStr } = useParams<RouteParams>();
  const index = Number(indexStr);

  const g = mk8sDraft.provider_gcp;
  const np = g.nodePools.find((np) => np.index === index);

  const [recommendedOptions, setRecommendedOptions] = React.useState<string[]>([]);
  React.useEffect(() => {
    fetchRecommendedOptions();
  }, []);
  async function fetchRecommendedOptions() {
    try {
      const { data: schemaRes } = await request({ url: "/catalog/schema/mk8s" });
      const images: string[] = schemaRes.keys.spec.keys.provider.keys.gcp.keys.image.keys.recommended.allow;
      setRecommendedOptions(images);
    } catch (e) {
      setRecommendedOptions([]);
    }
  }

  if (!np) {
    return <Navigate to={`${pathname.split("/-node-pools")[0]}/-node-pools`} />;
  }

  const pre = `gcp.nodePools[${index}].`;

  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.gcp.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.gcp.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.gcp.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.gcp.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.gcp.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.gcp.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.gcp.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  return (
    <>
      <div>
        <NGFormElement
          name={`${pre}name`}
          label={np.name.label}
          value={np.name.value}
          onChange={np.name.setValue}
          required
          error={!g.isNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
        />
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}machineType`}
            label={np.machineType.label}
            value={np.machineType.value}
            onChange={np.machineType.setValue}
            required={np.machineType.isRequired}
            error={np.machineType.error}
            innerButtons={[
              { title: "Browse", onClick: () => setBrowserKey("machineTypes"), render: () => <MoreHorizontal /> },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}machineType`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.machineType.setValue}
              path={`spec.provider.gcp.nodePools[${index}].machineType`}
            />
          </div>
        </div>
        <NGSwitch
          className="mb-4"
          name={`${pre}assignPublicIP`}
          value={np.assignPublicIP}
          onChange={(value) => np.setAssignPublicIp(value)}
        >
          <NGLabelText>Assign Public IP</NGLabelText>
        </NGSwitch>
        <NGSwitch
          className="mb-4"
          name={`${pre}preemptible`}
          value={np.preemptible}
          onChange={(value) => np.setPreemptible(value)}
        >
          <NGLabelText>Preemptible</NGLabelText>
        </NGSwitch>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}zone`}
            label={np.zone.label}
            value={np.zone.value}
            onChange={np.zone.setValue}
            required={np.zone.isRequired}
            error={np.zone.error}
            innerButtons={[
              { title: "Browse", onClick: () => setBrowserKey("zones"), render: () => <MoreHorizontal /> },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}zone`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.zone.setValue}
              path={`spec.provider.gcp.nodePools[${index}].zone`}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}subnet`}
            label={np.subnet.label}
            value={np.subnet.value}
            onChange={np.subnet.setValue}
            required={np.subnet.isRequired}
            error={np.subnet.error}
            innerButtons={[
              { title: "Browse", onClick: () => setBrowserKey("subnets"), render: () => <MoreHorizontal /> },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}subnet`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.subnet.setValue}
              path={`spec.provider.gcp.nodePools[${index}].subnet`}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}localPersistentDisks`}
            label={np.localPersistentDisks.label}
            value={np.localPersistentDisks.value}
            onChange={np.localPersistentDisks.setValue}
            required={np.localPersistentDisks.isRequired}
            error={np.localPersistentDisks.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}localPersistentDisks`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.localPersistentDisks.setValue}
              path={`spec.provider.gcp.nodePools[${index}].localPersistentDisks`}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}minSize`}
            label={np.minSize.label}
            value={np.minSize.value}
            onChange={np.minSize.setValue}
            required={np.minSize.isRequired}
            error={np.minSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}minSize`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.minSize.setValue}
              path={`spec.provider.gcp.nodePools[${index}].minSize`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}maxSize`}
            label={np.maxSize.label}
            value={np.maxSize.value}
            onChange={np.maxSize.setValue}
            required={np.maxSize.isRequired}
            error={np.maxSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}maxSize`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.maxSize.setValue}
              path={`spec.provider.gcp.nodePools[${index}].maxSize`}
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NGFormLabel name={`${pre}overrideImage.recommended`}>Override Image</NGFormLabel>
        </div>
        <NGRadioGroup
          value={np.overrideImage.type}
          onChange={(value) => np.overrideImage.setType(value as any)}
          options={[
            { label: "Recommended", value: "recommended" },
            { label: "Family", value: "family" },
            { label: "Exact", value: "exact" },
          ]}
        />
        {np.overrideImage.type === "recommended" ? (
          <div className="mb-4 flex items-start gap-2">
            <NGSelect
              name={`${pre}overrideImage.recommended`}
              style={{ width: 450, minWidth: 450 }}
              value={np.overrideImage.recommended}
              onChange={np.overrideImage.setRecommended}
              options={recommendedOptions.map((v) => ({ label: v, value: v }))}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={
                formData.get(`${pre}overrideImage.recommended`).touched ||
                formData.get(`${pre}overrideImage.exact`).touched ||
                formData.get(`${pre}overrideImage.familyProject`).touched ||
                formData.get(`${pre}overrideImage.familyFamily`).touched
              }
              dryRunResponse={mk8sDryRun.response}
              path={`spec.provider.gcp.nodePools[${index}].overrideImage`}
              paths={[`spec.provider.gcp.nodePools[${index}].overrideImage.recommended`]}
            />
          </div>
        ) : null}
        {np.overrideImage.type === "exact" ? (
          <div className="mb-4 flex items-start gap-2">
            <NGInput
              name={`${pre}overrideImage.exact`}
              style={{ width: 450, minWidth: 450 }}
              value={np.overrideImage.exact}
              onChange={(e) => np.overrideImage.setExact(e.target.value)}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={
                formData.get(`${pre}overrideImage.recommended`).touched ||
                formData.get(`${pre}overrideImage.exact`).touched ||
                formData.get(`${pre}overrideImage.familyProject`).touched ||
                formData.get(`${pre}overrideImage.familyFamily`).touched
              }
              dryRunResponse={mk8sDryRun.response}
              path={`spec.provider.gcp.nodePools[${index}].overrideImage`}
              paths={[`spec.provider.gcp.nodePools[${index}].overrideImage.exact`]}
            />
          </div>
        ) : null}
        {np.overrideImage.type === "family" ? (
          <>
            <NGFormLabel name={`${pre}overrideImage.familyProject`} required invalid={!np.overrideImage.familyProject}>
              Family | Project
            </NGFormLabel>
            <div className="mb-4 flex items-start gap-2">
              <NGInput
                name={`${pre}overrideImage.familyProject`}
                style={{ width: 450, minWidth: 450 }}
                value={np.overrideImage.familyProject}
                invalid={!np.overrideImage.familyProject}
                onChange={(e) => np.overrideImage.setFamilyProject(e.target.value)}
              />
              <DryRunAlert
                key={mk8sDryRun.key}
                canShow={
                  formData.get(`${pre}overrideImage.recommended`).touched ||
                  formData.get(`${pre}overrideImage.exact`).touched ||
                  formData.get(`${pre}overrideImage.familyProject`).touched ||
                  formData.get(`${pre}overrideImage.familyFamily`).touched
                }
                dryRunResponse={mk8sDryRun.response}
                path={`spec.provider.gcp.nodePools[${index}].overrideImage`}
                paths={[
                  `spec.provider.gcp.nodePools[${index}]overrideImage.family`,
                  `spec.provider.gcp.nodePools[${index}]overrideImage.family.project`,
                ]}
              />
            </div>
            <NGFormLabel name={`${pre}overrideImage.familyFamily`} required invalid={!np.overrideImage.familyFamily}>
              Family | Family
            </NGFormLabel>
            <div className="mb-4 flex items-start gap-2">
              <NGInput
                name={`${pre}overrideImage.familyFamily`}
                style={{ width: 450, minWidth: 450 }}
                value={np.overrideImage.familyFamily}
                invalid={!np.overrideImage.familyFamily}
                onChange={(e) => np.overrideImage.setFamilyFamily(e.target.value)}
              />
              <DryRunAlert
                key={mk8sDryRun.key}
                canShow={
                  formData.get(`${pre}overrideImage.recommended`).touched ||
                  formData.get(`${pre}overrideImage.exact`).touched ||
                  formData.get(`${pre}overrideImage.familyProject`).touched ||
                  formData.get(`${pre}overrideImage.familyFamily`).touched
                }
                dryRunResponse={mk8sDryRun.response}
                path={`spec.provider.gcp.nodePools[${index}].overrideImage`}
                paths={[
                  `spec.provider.gcp.nodePools[${index}].overrideImage.family`,
                  `spec.provider.gcp.nodePools[${index}].overrideImage.family.family`,
                ]}
              />
            </div>
          </>
        ) : null}

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}bootDiskSize`}
            label={np.bootDiskSize.label}
            value={np.bootDiskSize.value}
            onChange={np.bootDiskSize.setValue}
            required={np.bootDiskSize.isRequired}
            error={np.bootDiskSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={formData.get(`${pre}bootDiskSize`).touched}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.bootDiskSize.setValue}
              path={`spec.provider.gcp.nodePools[${index}].bootDiskSize`}
            />
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className="mb-8"
          label="Labels"
          labelRender={() => {
            return (
              <>
                {np.labels.items.some((l) => l.firstValue === "cpln.io/nodeType" && l.secondValue === "core") ? null : (
                  <NGButton
                    variant="primary"
                    className="ml-2"
                    onClick={() => {
                      const id = np.labels.add();
                      const item = np.labels.items.find((i) => i.id === id);
                      if (item) {
                        item.setFirstValue("cpln.io/nodeType");
                        item.setSecondValue("core");
                      }
                    }}
                    size={`small`}
                  >
                    Use this nodepool as BYOK core
                  </NGButton>
                )}
              </>
            );
          }}
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
        <NGButton
          style={{ width: 220 }}
          variant={"danger"}
          outlined
          onClick={() => {
            g.removeNodePoolAt(np.index);
            navigate(`${pathname.split("/-node-pools")[0]}/-node-pools`);
          }}
        >
          Delete Node Pool
        </NGButton>
      </div>
      {browserKey === "machineTypes" ? (
        <NGProviderBrowser
          title="Browse GCP Machine Types"
          initialValue={np.machineType.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "machineTypes",
                cluster: mk8sDraft.asObject,
              },
            });
            return data;
          }}
          onOk={(value) => {
            np.machineType.setValue(value as string);
          }}
          onClose={() => setBrowserKey("")}
          distinctFilters={["CPU Count", "Memory", "GPU Type", "GPU Count", "Arch"]}
          labels={[
            "Name",
            "CPU Count",
            "Memory",
            "GPU Type",
            "GPU Count",
            "Max Local SSDs",
            "Max Persistent Disks",
            "Arch",
            "Zones",
          ]}
          sortable
          getData={(item: any) => {
            let gpuType = "";
            let gpuCount = "";
            if (
              item.accelerators &&
              Array.isArray(item.accelerators) &&
              item.accelerators.length > 0 &&
              item.accelerators[0].guestAcceleratorType
            ) {
              gpuType = item.accelerators[0].guestAcceleratorType;
            }
            if (
              item.accelerators &&
              Array.isArray(item.accelerators) &&
              item.accelerators.length > 0 &&
              item.accelerators[0].guestAcceleratorCount
            ) {
              gpuCount = item.accelerators[0].guestAcceleratorCount;
            }

            return [
              item.name,
              item.name,
              item.guestCpus,
              [item.memoryMb, `${item.memoryMb}Mb`],
              [gpuType, gpuType || "-"],
              [gpuCount, gpuCount || "-"],
              [item.maxLocalSsds, `${item.maxLocalSsds || "-"}`],
              [item.maximumPersistentDisks, `${item.maximumPersistentDisks || "-"}`],
              item.architecture,
              item.zones.map((x) => x.split("-").at(-1)).join(", "),
            ];
          }}
        />
      ) : null}
      {browserKey === "zones" ? (
        <NGProviderBrowser
          width={1550}
          title="Browse GCP Zones"
          initialValue={np.zone.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: `zones`,
                cluster: mk8sDraft.asObject,
              },
            });
            return data;
          }}
          onOk={(value) => {
            np.zone.setValue(value as string);
          }}
          onClose={() => setBrowserKey("")}
          rightAlign={["Cost per Month"]}
          labels={["Name", "Region", "Available CPU Platforms"]}
          sortable
          getData={(item: any) => {
            return [item.name, item.name, item.region.split(`/regions/`)[1], item.availableCpuPlatforms.join(",")];
          }}
        />
      ) : null}
      {browserKey === "subnets" ? (
        <NGProviderBrowser
          title="Browse GCP Subnets"
          initialValue={np.subnet.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "subnets",
                cluster: mk8sDraft.asObject,
              },
            });
            return data;
          }}
          onOk={(value) => np.subnet.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Region", "IP CIDR Range"]}
          getData={(item: any) => {
            return [item.name, item.name, item.region.split(`/regions/`)[1], item.ipCidrRange];
          }}
        />
      ) : null}
    </>
  );
};

export const Mk8sCreateProviderGCPNodePool = observer(Mk8sCreateProviderGCPNodePoolRaw);
